<script setup>
import { computed, onMounted, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import ConfirmDialog from 'primevue/confirmdialog'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { themeImagesApi } from '@/services/themeImagesApi'

const toast = useToast()
const confirm = useConfirm()
const loading = ref(false)
const uploadConfigured = ref(true)
const groups = ref([])
const busy = ref('')
const activeGroup = ref('')
const drafts = ref({})
const newDrafts = ref({})
const rowFile = ref(null)
const addFile = ref(null)
const fileTarget = ref(null)
const preview = ref({ visible: false, src: '', title: '' })
const previewSize = ref('')
const addPreview = ref('')

function openPreview(item) {
  openPreviewSrc(item.image, 'Обложка баннера')
}

function openPreviewSrc(src, title) {
  previewSize.value = ''
  preview.value = { visible: true, src, title: title || 'Обложка баннера' }
}

function onPreviewLoad(event) {
  const img = event && event.target
  if (img && img.naturalWidth) previewSize.value = `${img.naturalWidth} × ${img.naturalHeight} px`
}

const groupNames = computed(() => groups.value.map((group) => group.name))

const current = computed(
  () => groups.value.find((group) => group.name === activeGroup.value) || { name: '', items: [] }
)

function draftFor(item) {
  return drafts.value[item.id] || { link: item.link || '', lesson: item.lesson || '' }
}

function setDraft(item, patch) {
  drafts.value = { ...drafts.value, [item.id]: { ...draftFor(item), ...patch } }
}

function newDraft() {
  if (!newDrafts.value[activeGroup.value]) {
    newDrafts.value = {
      ...newDrafts.value,
      [activeGroup.value]: { link: '', lesson: '', image: '', file: null },
    }
  }
  return newDrafts.value[activeGroup.value]
}

function setNewDraft(patch) {
  newDraft()
  newDrafts.value = {
    ...newDrafts.value,
    [activeGroup.value]: { ...newDrafts.value[activeGroup.value], ...patch },
  }
}

function apiMessage(error) {
  return error.response?.data?.error || error.message
}

function fail(error) {
  toast.add({ severity: 'error', summary: 'Ошибка', detail: apiMessage(error), life: 5000 })
}

function resetDrafts() {
  const next = {}
  groups.value.forEach((group) => {
    group.items.forEach((item) => {
      next[item.id] = { link: item.link || '', lesson: item.lesson || '' }
    })
  })
  drafts.value = next
}

async function load() {
  loading.value = true
  try {
    const { data } = await themeImagesApi.load()
    groups.value = data.groups || []
    uploadConfigured.value = data.uploadConfigured !== false
    resetDrafts()
    if (!groupNames.value.includes(activeGroup.value)) {
      activeGroup.value = groupNames.value[0] || ''
    }
  } catch (error) {
    fail(error)
  } finally {
    loading.value = false
  }
}

function pickRowFile(item) {
  fileTarget.value = { kind: 'row', item }
  rowFile.value && rowFile.value.click()
}

function pickAddFile() {
  fileTarget.value = { kind: 'add' }
  addFile.value && addFile.value.click()
}

async function onFile(event) {
  const file = event.target.files && event.target.files[0]
  event.target.value = ''
  const target = fileTarget.value
  fileTarget.value = null
  if (!file || !target) return

  if (target.kind === 'row') {
    const item = target.item
    busy.value = `file:${item.id}`
    try {
      await themeImagesApi.uploadBannerFile(item.id, file)
      toast.add({ severity: 'success', summary: 'Картинка заменена', life: 2500 })
      await load()
    } catch (error) {
      fail(error)
    } finally {
      busy.value = ''
    }
    return
  }

  setNewDraft({ file, image: '' })
  if (addPreview.value) URL.revokeObjectURL(addPreview.value)
  addPreview.value = URL.createObjectURL(file)
  toast.add({ severity: 'info', summary: 'Файл выбран', detail: file.name, life: 2500 })
}

async function saveItem(item) {
  const draft = draftFor(item)
  busy.value = `save:${item.id}`
  try {
    await themeImagesApi.updateBanner(item.id, { link: draft.link, lesson: draft.lesson })
    toast.add({ severity: 'success', summary: 'Сохранено', life: 2500 })
    await load()
  } catch (error) {
    fail(error)
  } finally {
    busy.value = ''
  }
}

function removeItem(item) {
  confirm.require({
    message: 'Удалить этот баннер? Он сразу исчезнет с сайта.',
    header: 'Удаление баннера',
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Отмена',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Удалить',
      severity: 'danger',
    },
    accept: () => doRemoveItem(item),
  })
}

async function doRemoveItem(item) {
  busy.value = `del:${item.id}`
  try {
    await themeImagesApi.deleteBanner(item.id)
    toast.add({ severity: 'success', summary: 'Баннер удалён', life: 2500 })
    await load()
  } catch (error) {
    fail(error)
  } finally {
    busy.value = ''
  }
}

async function addBanner() {
  const draft = newDraft()
  const payload = { link: draft.link, lesson: draft.lesson }
  if (draft.file) payload.file = draft.file
  else if (draft.image) payload.image = draft.image

  if (!payload.file && !payload.image) {
    toast.add({ severity: 'warn', summary: 'Нужна картинка', detail: 'Выберите файл или вставьте адрес', life: 4000 })
    return
  }

  busy.value = 'add'
  try {
    await themeImagesApi.addBanner(activeGroup.value, payload)
    newDrafts.value = { ...newDrafts.value, [activeGroup.value]: { link: '', lesson: '', image: '', file: null } }
    toast.add({ severity: 'success', summary: 'Баннер добавлен', life: 2500 })
    await load()
  } catch (error) {
    fail(error)
  } finally {
    busy.value = ''
  }
}

onMounted(load)
</script>

<template>
  <p v-if="loading">Загрузка…</p>
  <div v-else class="theme-images">
    <ConfirmDialog />
    <p v-if="!uploadConfigured" class="warn">
      Загрузка файла выключена: на сервере нет GC_LOGIN/GC_PASSWORD. Адрес уже залитого файла GetCourse вставить можно.
    </p>
    <p class="hint">
      Здесь баннеры добавляются и удаляются. Если у баннера заполнено «Название урока», он ведёт на этот урок,
      иначе открывает «Ссылку». Порядок — как в списке.
    </p>

    <input ref="rowFile" class="file-input" type="file" accept="image/*" @change="onFile" />
    <input ref="addFile" class="file-input" type="file" accept="image/*" @change="onFile" />

    <div class="tabs">
      <Button
        v-for="name in groupNames"
        :key="name"
        :label="name"
        size="small"
        :severity="activeGroup === name ? 'primary' : 'secondary'"
        :outlined="activeGroup !== name"
        @click="activeGroup = name"
      />
    </div>

    <p v-if="!current.items.length" class="hint">
      На этом месте баннеров нет. Добавьте первый — он сразу появится на сайте.
    </p>

    <div class="grid">
      <article v-for="item in current.items" :key="item.id" class="card">
        <img
          :src="item.image"
          alt="Баннер"
          class="thumb"
          title="Нажмите, чтобы увеличить"
          @click="openPreview(item)"
        />
        <div class="body">
          <label class="field">
            <span>Название урока</span>
            <InputText
              :model-value="draftFor(item).lesson"
              class="w-full"
              placeholder="не обязательно"
              @update:model-value="setDraft(item, { lesson: $event })"
            />
          </label>
          <label class="field">
            <span>Ссылка</span>
            <InputText
              :model-value="draftFor(item).link"
              class="w-full"
              placeholder="https://get-nechto.ru/... или /lesson/view?id=..."
              @update:model-value="setDraft(item, { link: $event })"
            />
          </label>
          <div class="actions">
            <Button
              label="Просмотр"
              icon="pi pi-search"
              size="small"
              text
              @click="openPreview(item)"
            />
            <Button
              label="Заменить файл"
              icon="pi pi-upload"
              size="small"
              severity="secondary"
              :disabled="!!busy"
              :loading="busy === `file:${item.id}`"
              @click="pickRowFile(item)"
            />
            <Button
              label="Сохранить"
              size="small"
              :disabled="!!busy"
              :loading="busy === `save:${item.id}`"
              @click="saveItem(item)"
            />
            <Button
              label="Удалить"
              icon="pi pi-trash"
              size="small"
              text
              severity="danger"
              :disabled="!!busy"
              :loading="busy === `del:${item.id}`"
              @click="removeItem(item)"
            />
          </div>
        </div>
      </article>
    </div>

    <section class="add-box">
      <h4>Добавить баннер — {{ current.name }}</h4>
      <div class="add-grid">
        <label class="field">
          <span>Картинка (файл)</span>
          <div class="actions">
            <Button
              label="Выбрать файл"
              icon="pi pi-upload"
              size="small"
              severity="secondary"
              :disabled="!!busy"
              @click="pickAddFile"
            />
            <span v-if="newDraft().file" class="badge">{{ newDraft().file.name }}</span>
          </div>
        </label>
        <label class="field">
          <span>или адрес GetCourse</span>
          <InputText
            :model-value="newDraft().image"
            class="w-full"
            placeholder="https://fs.getcourse.ru/..."
            @update:model-value="setNewDraft({ image: $event, file: null }); addPreview = $event"
          />
        </label>
        <label class="field">
          <span>Название урока</span>
          <InputText
            :model-value="newDraft().lesson"
            class="w-full"
            placeholder="не обязательно"
            @update:model-value="setNewDraft({ lesson: $event })"
          />
        </label>
        <label class="field">
          <span>Ссылка</span>
          <InputText
            :model-value="newDraft().link"
            class="w-full"
            placeholder="https://get-nechto.ru/..."
            @update:model-value="setNewDraft({ link: $event })"
          />
        </label>
      </div>
      <div v-if="addPreview" class="add-preview">
        <img
          :src="addPreview"
          alt="Предпросмотр"
          class="thumb thumb-big"
          title="Нажмите, чтобы увеличить"
          @click="openPreviewSrc(addPreview, 'Новый баннер')"
        />
        <span class="hint">так баннер будет выглядеть на странице</span>
      </div>
      <Button
        label="Добавить баннер"
        icon="pi pi-plus"
        size="small"
        :disabled="!!busy"
        :loading="busy === 'add'"
        @click="addBanner"
      />
    </section>

    <Dialog
      v-model:visible="preview.visible"
      :header="preview.title"
      modal
      :style="{ width: '90vw', maxWidth: '1100px' }"
    >
      <img :src="preview.src" alt="Обложка" class="preview-img" @load="onPreviewLoad" />
      <p v-if="previewSize" class="hint">{{ previewSize }}</p>
      <div class="actions">
        <Button label="Закрыть" size="small" severity="secondary" outlined @click="preview.visible = false" />
        <a :href="preview.src" target="_blank" rel="noopener" class="open-original">Открыть в новой вкладке</a>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.theme-images {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}
.hint,
.warn {
  margin: 0;
  color: var(--p-text-muted-color, #6b7280);
  max-width: 900px;
}
.warn {
  color: var(--p-orange-600, #c2410c);
}
.tabs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 0.75rem;
}
.card {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 8px;
}
.card img {
  width: 96px;
  height: 64px;
  object-fit: cover;
  border-radius: 4px;
  background: #f3f4f6;
  flex: 0 0 auto;
}
.thumb {
  cursor: zoom-in;
}
.thumb:hover {
  outline: 2px solid var(--p-primary-color, #3b82f6);
  outline-offset: 1px;
}
.thumb-big {
  width: 100%;
  max-width: 560px;
  height: auto;
  border-radius: 6px;
}
.add-preview {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.preview-img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 6px;
  background: #f3f4f6;
}
.open-original {
  font-size: 0.85rem;
  color: var(--p-primary-color, #2563eb);
}
.body {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
  flex: 1;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.8rem;
  color: var(--p-text-muted-color, #6b7280);
}
.actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}
.add-box {
  border: 1px dashed var(--p-content-border-color, #d1d5db);
  border-radius: 8px;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}
.add-box h4 {
  margin: 0;
}
.add-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.6rem;
}
.badge {
  font-size: 0.8rem;
  color: var(--p-green-700, #15803d);
}
.file-input {
  display: none;
}
.w-full {
  width: 100%;
}
</style>
